/*
* This file contains redux actions for analytics for general info about current page of listing page
* and for products data on the current page of listing page
* the type attributes are equivalent to types of actions defined in @dc/data-layer
* */
import { satelliteTrack } from '@dc/data-layer/dist/utils';

import { getActiveListingPage } from '../reduck/selectors';
import { convertProductsData } from '../dataConverters/analyticsConverter';

// set general analytics data for listing page which is currently shown in user's browser
// it's stored in window.digitalData.page
export function setPageAnalyticsInDigitalData(dataFromBrowserCache = false, dataForAnalytics = {}, firstLoad = false) {
  return (dispatch, getState) => {
    const activeListingPage = getActiveListingPage(getState());

    // clear events array and sponsoredProducts array for new page
    dispatch({
      type: 'INIT_DIGITAL_DATA', // TODO: add specific event for clearing events array do @dc/data-layer
      digitalData: {
        events: [],
        sponsoredProducts: undefined
      }
    });


    if (firstLoad && activeListingPage.searchListingPage && activeListingPage.productList.length === 0) {
      // empty search result page
      dispatch({
        type: 'SET_PAGE',
        page: {
          category: {
            categoryId: 'xx',
            primaryCategory: null,
            marketId: 'xx',
            market: null,
            segmentId: 'xx',
            segment: null,
            universeId: null,
            universe: 'search results page'
          },
          pageName: 'empty search results page',
          pageType: 'search results page',
          onSiteSearchTerm: getOnSiteSearchTerm(activeListingPage.pageAttributes),
          onSiteSearchType: 'Keyword Searches - Failed',
          onSiteSearchReturnedResultsTerm: activeListingPage.pageAttributes.query,
          newListingPage: true,
          dataFromBrowserCache: dataFromBrowserCache,
          pageNumber: null,
          numberOfSponsoredProducts: 0,
          onSiteSearchResults: 0,
          pageUrl: window.location.href,
          queryString: window.location.search,
          referringURL: document.referrer,
          refinement: []
        }
      });
    } else {
      dispatch({
        type: 'SET_PAGE',
        page: {
          category: {
            categoryId: !activeListingPage.searchListingPage
              ? activeListingPage.ucmsCategorization.categoryId : 'xx',
            primaryCategory: !activeListingPage.searchListingPage
              ? activeListingPage.ucmsCategorization.categoryName : null,
            marketId: !activeListingPage.searchListingPage
              ? activeListingPage.ucmsCategorization.marketId : 'xx',
            market: !activeListingPage.searchListingPage
              ? activeListingPage.ucmsCategorization.marketName : null,
            segmentId: !activeListingPage.searchListingPage
              ? activeListingPage.ucmsCategorization.segmentId : 'xx',
            segment: !activeListingPage.searchListingPage
              ? activeListingPage.ucmsCategorization.segmentName : null,
            universeId: !activeListingPage.searchListingPage
              ? activeListingPage.ucmsCategorization.universeId : null,
            universe: !activeListingPage.searchListingPage
              ? activeListingPage.ucmsCategorization.universeName : 'search results page'
          },
          pageName: !activeListingPage.searchListingPage
            ? `${activeListingPage.ucmsCategorization.universeName}: ` +
            `${activeListingPage.ucmsCategorization.categoryName}: ` +
            `${activeListingPage.ucmsCategorization.categoryName}: ` +
            `${activeListingPage.ucmsCategorization.categoryName}`
            : 'search results page',
          pageType: activeListingPage.searchListingPage ? 'search results page' : 'Segment',
          onSiteSearchTerm: activeListingPage.searchListingPage ? getOnSiteSearchTerm(activeListingPage.pageAttributes) : null,
          onSiteSearchType: 'Keyword Searches - Success',
          onSiteSearchReturnedResultsTerm: activeListingPage.searchListingPage ? activeListingPage.pageAttributes.query : '',
          newListingPage: true,
          dataFromBrowserCache: dataFromBrowserCache,
          pageNumber:
            activeListingPage.pagination.findIndex((paginationItem) => paginationItem.selected) !== -1
              ? Number(activeListingPage.pagination.find((paginationItem) => paginationItem.selected).page)
              : null,
          numberOfSponsoredProducts: 0,
          onSiteSearchResults: activeListingPage.categoryTotalProductCount,
          pageUrl: window.location.href,
          queryString: window.location.search,
          rankAction: activeListingPage.sortByOptions.items.find((item) => item.selected).label === 'Relevance'
            ? 'Default' : 'Applied',
          rankMethod: activeListingPage.sortByOptions.items.find((item) => item.selected).label,
          referringURL: document.referrer,
          refinement: createRefinementArrayForPage(activeListingPage, dataForAnalytics)
        }
      });
    }

    if (!firstLoad) {
      // triggers ADOBE LAUNCH to update when data updated by AJAX call and not by first page load
      // timeout to "make sure" redux dispatch was propagated
      setTimeout((_) => satelliteTrack('refinementUpdate'), 0);
    } else {
      // this informs ADOBE LAUNCH that the content of the page has been fully loaded after page load
      // timeout to "make sure" redux dispatch was propagated
      setTimeout((_) => satelliteTrack('appPageNameUpdate'), 0);
    }
  };
}

// set analytics data for products on listing page which is currently shown in user's browser
// it's stored in window.digitalData.products
export function setProductsAnalyticsInDigitalData() {
  return (dispatch, getState) => {
    const activeListingPage = getActiveListingPage(getState());
    const products = convertProductsData(activeListingPage.productList, getState().listingPage.staticData.ucmsHierarchy);

    dispatch({
      type: 'SET_PRODUCTS',
      products: products
    });
  };
}

// set analytics data concerning availability of each product which is currently shown in user's browser
// after postcode checking
// it's stored in window.digitalData.events
export function setProductsPlaceAvailabilitiesAnalytics(availabilities, activeListingPage) {
  return (dispatch) => {

    const products = availabilities.map((availability) => ({
      productID: availability.getProductId(),
      productSKU: activeListingPage.productList.find((product) => product.id === availability.getProductId()).sku,
      productName: activeListingPage.productList.find((product) => product.id === availability.getProductId()).title,
      availabilityStatus:
        `${availability.getDeliveryState()}|` +
        `${availability.getCollectionState()}`,
      soonestDelivery: availability.getAnalytics().soonestDelivery,
      soonestFreeDelivery: availability.getAnalytics().soonestFreeDelivery
    }));

    dispatch({
      type: 'EVENTS_STOCK_AVAILABILITY',
      payload: { products }
    });
  };
}

// set number of sponsored product after they're loaded to current listing page
// it's stored in window.page.numberOfSponsoredProducts
export function setSponsoredProductsCountAnalytics(totalSponsoredProductsCount) {
  return (dispatch) => {
    dispatch({
      type: 'COUNT_SPONSORED_PRODUCTS',
      payload: totalSponsoredProductsCount
    });
  };
}


// set sponsored products data after they're loaded to current listing page
// it's stored in window.sponsoredProducts
export function setSponsoredProductsDataAnalytics(sponsoredProducts) {
  return (dispatch) => {
    dispatch({
      type: 'ADD_SPONSORED_PRODUCTS',
      payload: sponsoredProducts
    });
  };
}

// set analytics data when fetching data for listing page fails
// it's stored in window.events
export function setFetchingPageDataFailedAnalytics(fetchResult) {
  return (dispatch) => {
    let errorMessage = 'Unknown error occurred';

    if (fetchResult.errorObject && fetchResult.errorObject.message) {
      errorMessage = fetchResult.errorObject.message;
    } else if (fetchResult.customErrorMessage) {
      errorMessage = fetchResult.customErrorMessage;
    }

    dispatch({
      type: 'EVENTS_FAILURE_AJAX_FILTERS_RESPONSE',
      payload: {
        errorType: errorMessage,
        interactionType: 'filter update'
      }
    });
  };
}

function getOnSiteSearchTerm(pageAttributes) {
  if (pageAttributes.query) {
    return pageAttributes.query;
  }

  if (pageAttributes?.filterAttributes?.tag) {
    return `-${pageAttributes.filterAttributes.tag}-`;
  }

  if (pageAttributes?.filterAttributes?.externalref) {
    return pageAttributes.filterAttributes.externalref.replace(/%(25)?7c/g, '|');
  }

  if (pageAttributes?.filterAttributes?.id) {
    return pageAttributes.filterAttributes.id.replace(/%(25)?7c/g, '|');
  }

  return '';
}

function createRefinementArrayForPage(activeListingPage, dataForAnalytics = {}) {
  const refinement = [];

  if (!activeListingPage.searchListingPage) {
    // segment name for segment listing page is added
    refinement.push({
      searchRefinementAttribute: 'Product Category',
      searchRefinementValue: activeListingPage.ucmsCategorization.segmentName
    });
  }


  // price filter
  // it's added to the analytics data only if a price range is defined
  if (typeof activeListingPage.pageAttributes.price_min !== 'undefined') {
    let searchRefinementValue;

    if (typeof activeListingPage.pageAttributes.price_max !== 'undefined') {
      searchRefinementValue = `${activeListingPage.pageAttributes.price_min}-${activeListingPage.pageAttributes.price_max}`;
    } else {
      searchRefinementValue = `Over ${activeListingPage.pageAttributes.price_min}`;
    }
    refinement.push({
      searchRefinementAttribute: 'Price',
      searchRefinementValue
    });
  }

  // other filters from searchLabs
  // a filter item is added to analytics data only if it has one or more values selected
  activeListingPage.filtersFromSearchLabs.forEach((filterItem) => {
    if (filterItem.values.findIndex((value) => value.selected) !== -1) {
      let searchRefinementValue = '';

      filterItem.values.filter((value) => value.selected).forEach((value, index, valuesArray) => {
        searchRefinementValue += `${value.label}${index !== valuesArray.length - 1 ? '|' : ''}`;
      });

      refinement.push({
        searchRefinementAttribute: filterItem.id === 'segment_id' ? 'Product Category' : filterItem.label,
        searchRefinementValue
      });
    }
  });

  // when a filter was removed on the previous page by clicking on a blue tag in the 'Active filters' panel,
  // we add type label of the removed filter and value label of the filter to the refinement array
  if (dataForAnalytics?.removedFilter) {
    refinement.push({
      searchRefinementAttribute: `${dataForAnalytics.removedFilter.filterTypeLabel} Removed`,
      searchRefinementValue: `${dataForAnalytics.removedFilter.filterValueLabel} Removed`
    });
  }


  return refinement;
}

// Dispatch when user selects search term  from suggestions
export function selectedSuggestion(suggestionType) {
  return (dispatch) => {
    dispatch({
      type: 'SELECT_SUGGESTED_ITEM',
      payload: {
        suggestionType
      }
    });
  };
}
